import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, CtaBand } from "@/components/Blocks";
import { SolutionsTabs, Systems } from "@/pages/Home";

const LAYERS = [
  ["Engage", "Website, SEO, catalogues and inquiry flows bring qualified buyers to your sales team."],
  ["Sell", "CRM, quotations and B2B portals turn inquiries into orders and repeat business."],
  ["Make", "ERP, production planning, MES and quality tools run the plant on accurate data."],
  ["Deliver", "Inventory, dispatch, dealer and supplier systems move goods through your network."],
  ["Improve", "Dashboards, IoT data and AI-assisted reporting show where to improve next."],
];

export default function Solutions() {
  const crumbs = [["Home", "/"], ["Solutions", "/solutions"]];
  return (
    <>
      <Seo title="Manufacturing Digital Transformation & Software Solutions" description="Software and digital transformation solutions for manufacturers: connected ERP, CRM, MES, QMS, inventory, dealer management, BI dashboards and custom workflow applications." path="/solutions" jsonLd={[breadcrumbSchema(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Software & digital transformation" lines={["Connected Systems", "for Modern", "Industry"]} intro="Digital transformation for a manufacturer is not one big software purchase. It is a series of practical steps that connect how you win customers, plan production, manage materials and deliver orders. We help you choose and build the right next step." img="line" imgAlt="Automated production line with robotic equipment in a factory" />
      <section className="wrap py-24 lg:py-32" data-testid="solutions-layers">
        <SectionHead eyebrow="The connected value chain" title="Five layers of an industrial digital business" lead="Each layer can be improved on its own. Connecting them gives you a single, reliable view of the business." />
        <ol className="mt-14 grid gap-px overflow-hidden rounded-md border border-stone bg-stone md:grid-cols-5">
          {LAYERS.map(([t, d], i) => (
            <Reveal as="li" key={t} delay={i * 0.06} className="bg-white p-7">
              <span className="font-mono text-xs text-rust-ink">L{i + 1}</span>
              <h3 className="mt-8 font-display text-2xl font-bold">{t}</h3>
              <p className="mt-3 text-[14px] leading-relaxed text-graphite">{d}</p>
            </Reveal>
          ))}
        </ol>
      </section>
      <section className="border-t border-stone bg-white py-24 lg:py-32" data-testid="solutions-functions">
        <div className="wrap">
          <SectionHead eyebrow="By business function" title="Technology for Every Stage of Your Industrial Business" lead="The final solution is always selected and scoped according to each client's requirements." />
          <SolutionsTabs />
        </div>
      </section>
      <Systems />
      <section className="wrap py-24 lg:py-32" data-testid="solutions-note">
        <div className="grid gap-10 lg:grid-cols-3">
          {[
            ["Custom where it matters", "We build custom software when your processes give you an advantage that packaged tools cannot support."],
            ["Integrated where possible", "If an existing ERP, accounting or CRM tool works well, we connect to it instead of replacing it."],
            ["Realistic about automation", "IoT and AI features are designed for monitoring, assistance and reporting, with human review and clear limits."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} className="border-t-2 border-charcoal pt-6">
              <h3 className="font-display text-xl font-bold">{t}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-graphite">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
